// eslint-disable-next-line
import { Tooltip, LinearProgress, Box, Link, Popover, Paper, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgressBar from "../../circularProgressBar/CircularProgressBar";
import { NotesTooltip } from "../../notesTooltip/NotesTooltip";
import EditIcon from "@mui/icons-material/Edit";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import DonutLargeIcon from "@mui/icons-material/DonutLarge";
import { useDivisionFeatureFlags } from "../../../stateContext/featureFlags/FeatureFlagProvider";
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  getNHCDateTime,
  textLengthValidator,
} from "../../../utils/functions";
import {
  getGridDateOperators,
  GridCellParams,
  GridColType,
  GridPreProcessEditCellProps,
  GridRenderEditCellParams,
} from "@mui/x-data-grid";

import BuyerListingGridEditText from "../../../pages/closings/buyerListing/buyerListingGridEditText";
import { GridEditDateCell } from "../../../pages/closings/buyerListing/buyerListingGridEditDateCell";
import { RevisedEcoeEditCell } from "../../../pages/closings/buyerListing/RevisedEcoeEditCell";
import { GridBaseColDef } from "@mui/x-data-grid/internals";
import BuyerListingOption from "../../../pages/closings/buyerListing/buyerListingOptions/buyerListingOptions";
import { BuyerListingActions } from "../../../pages/closings/buyerListing/buyerListingActions/buyerListingActions";
import { salesforceUrl } from "../../../api/apiUrl";
import { useUpdateHomeSite } from "../../../reactQuery/homeSites/homsites/homeSiteQuery";
import { useSelector } from "react-redux";
import { getAuth } from "../../../redux/auth/authSlice";
import { useCallback, useMemo, useState } from "react";
import { MilestoneTask } from "../../../api/homeSiteMilestones/types";
import { BuyerListingOptionsProvider } from "../../../pages/closings/buyerListing/buyerListingOptions/buyerListingOptionsProvider";
import { Dayjs } from "dayjs";
import {
  dateValidator,
  currencyValidator,
  emailValidator,
  ValidatorResponse,
  stringValidator,
  phoneValidator,
  dateTimeValidator,
  noteToSelfValidator,
  contactDateValidator,
} from "../../../utils/common/validator";

import { CONTACTMETHOD, LOANFOREIGNNATIONALLOAN, OSLLOANGRADE } from "./homesiteColumnConsts";
import { GridEditDateTimeCell } from "../../../pages/closings/buyerListing/buyerListingGridEditDateTimeCell";
import ContactsIcon from '@mui/icons-material/Contacts';
import { buildNHCDateTimePatch } from "../../../utils/common/buildNHCDateTimePatch";
import { HomeSitesDataIndex } from "../../../reactQuery/homeSitesDataIndex/types";

export enum TableColumnMap {
  BuyerListingFields = "buyerListingFields",
  BuyerListingBuyerFields = "buyerListingBuyerFields",
  BuyerListingHomeSiteFields = "buyerListingHomeSiteFields",
  BuyerListingMortgageTitleFields = "buyerListingLenderFields",
  BuyerListingOSLFields = "buyerListingOSLFields",
  ReportFields = "reportFields",
  TaskFields = "taskFields",
  ConciergeAssignmentFields = "conciergeAssignmentFields",
}

export interface HomesiteColDef extends GridBaseColDef {
  headerName: string;
  flex?: number;
  customFlex?: Partial<Record<TableColumnMap, number>>;
  customMinWidth?: Partial<Record<TableColumnMap, number>>;
  customWidth?: Partial<Record<TableColumnMap, number>>;
  filterable?: boolean;
  customHideable?: Partial<Record<TableColumnMap, boolean>>;
  editable?: boolean;
  type: GridColType;
  availableInTables?: Partial<Record<TableColumnMap, number>>;
  headerClassName: string;
  renderHeader?: () => JSX.Element;
  renderEditCell?: (params: GridRenderEditCellParams) => JSX.Element;
  valueGetter?: (value: any, row: any) => any;
  valueFormatter?: (params: any) => any;
  valueValidator?: (value: any) => ValidatorResponse;
  filterOperators?: any;
  onClick?: (event: React.MouseEvent, params: any) => void;
}

type MilestoneRowClickHandler = (row: any, milestoneName: string) => void;
let _milestoneRowClickHandler: MilestoneRowClickHandler | null = null;
let _milestoneRowClickHandledAt = 0;

const markMilestoneInteractionHandled = () => {
  _milestoneRowClickHandledAt = Date.now();
};

export const registerMilestoneRowClickHandler = (handler: MilestoneRowClickHandler | null) => {
  _milestoneRowClickHandler = handler;
};

export const wasMilestoneRowClickHandled = () => Date.now() - _milestoneRowClickHandledAt < 500;

interface MilestoneProgressCellProps {
  params: any;
}

const MilestoneProgressCell: React.FC<MilestoneProgressCellProps> = ({ params }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const progress = params.value || 0;
  const completed = params.row.milestoneProgressCompleted || 0;
  const total = params.row.milestoneProgressTotal || 0;
  const breakdown = params.row.milestoneProgressBreakdown || [];
  const radius = 16;
  const maxValue = total > 0 ? total : 10;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (breakdown.length > 0) {
      setAnchorEl(anchorEl ? null : event.currentTarget);
    }
  };

  const handleMilestoneRowClick = (milestoneName: string) => {
    markMilestoneInteractionHandled();
    setAnchorEl(null);
    _milestoneRowClickHandler?.(params.row, milestoneName);
  };

  const open = Boolean(anchorEl);

  const ringColor =
    progress >= 75 ? "#2e7d32" : progress >= 25 ? "#ed6c02" : "#d32f2f";

  const circleBar = (
    <CircularProgressBar
      maxValue={maxValue}
      selectedValue={completed}
      radius={radius}
      strokeWidth={2}
      activeStrokeColor={ringColor}
      inactiveStrokeColor="#e0e0e0"
      backgroundColor="#fff"
      textColor="#000"
      valueFontSize={Math.floor(radius / 2.2)}
      labelFontSize={Math.floor(radius / 3)}
      label=""
      anticlockwise={false}
      initialAngularDisplacement={0}
    />
  );

  if (breakdown.length === 0) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
        {circleBar}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
      <Box
        onClick={handleClick}
        sx={{ cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
      >
        {circleBar}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => {
          markMilestoneInteractionHandled();
          setAnchorEl(null);
        }}
        disableRestoreFocus
        disableScrollLock
        anchorOrigin={{ vertical: "center", horizontal: "right" }}
        transformOrigin={{ vertical: "center", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 0,
            ml: 1,
            borderRadius: 2,
            boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.18)",
            border: "1px solid rgba(0, 0, 0, 0.08)",
            overflow: "visible",
          },
        }}
      >
        <Paper elevation={0} sx={{ p: 0, minWidth: 300, maxWidth: 380 }}>
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: "1px solid rgba(0,0,0,0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "#f5f7fa",
              borderRadius: "8px 8px 0 0",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: "0.875rem", color: "#1a1a2e" }}>
              Milestone Progress
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                {completed}/{total} tasks ({progress}%)
              </Typography>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  markMilestoneInteractionHandled();
                  setAnchorEl(null);
                }}
                sx={{ p: 0.25 }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ px: 2, py: 1.5, maxHeight: 340, overflowY: "auto" }}>
            {breakdown.map((milestone: { name: string; completed: number; total: number; progress: number }, index: number) => (
              <Box key={index} onClick={() => handleMilestoneRowClick(milestone.name)} sx={{ mb: index < breakdown.length - 1 ? 1.5 : 0, cursor: "pointer", borderRadius: 1, px: 0.5, py: 0.5, "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" } }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, fontSize: "0.78rem", flex: 1, mr: 1, color: "#1a1a2e" }}>
                    {milestone.name}
                  </Typography>
                  <Typography variant="caption" sx={{ fontSize: "0.72rem", color: "text.secondary", minWidth: 56, textAlign: "right", fontWeight: 500 }}>
                    {milestone.completed}/{milestone.total}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={milestone.progress}
                  sx={{
                    height: 5,
                    borderRadius: 3,
                    backgroundColor: "#e8edf2",
                    "& .MuiLinearProgress-bar": {
                      borderRadius: 3,
                      backgroundColor:
                        milestone.progress === 100 ? "#4caf50" : milestone.progress >= 50 ? "#2196f3" : "#ff9800",
                    },
                  }}
                />
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              px: 2,
              py: 1,
              borderTop: "1px solid rgba(0,0,0,0.06)",
              backgroundColor: "#f5f7fa",
              borderRadius: "0 0 8px 8px",
              cursor: "pointer",
            }}
          >
            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem" }}>
              Click to view full milestone details
            </Typography>
          </Box>
        </Paper>
      </Popover>
    </Box>
  );
};

// Generic cell renderer component for clickable count columns
export const useHomesite = () => {
  const { userInfo } = useSelector(getAuth);
  const { showMilestoneProgress } = useDivisionFeatureFlags(userInfo?.userDivCode || "");
  const { mutateAsync: patchHomesite } = useUpdateHomeSite(
    userInfo.userDivCode
  );
  const customDateOperators = getGridDateOperators().filter(
    (operator) => operator.value !== "before"
  );

  const handleEdit = useCallback((params: any) => {
    console.log("Check Homesite Info", params);
  }, []);
  const GENERAL_TEXT_FIELD_MAX_LIMIT = "Exceeds max length of 50";
  const NOTE_TO_SELF_MAX_LIMIT = "Exceeds max length of 100";

  /** Create a patch for an existing parent path */
  const createPatchForExistingParent = (
    field: string,
    currVal: string,
    value: string
  ) => ({
    patches: [
      {
        op: "set",
        property: `/${field}`,
        from: currVal || null,
        to: value || null,
      },
    ],
  });

  /** Create a patch for a new parent path */
  const createPatchForNewParent = (
    field: string,
    fieldKey: string,
    value: string
  ) => {
    const parentPath = field.split("/").slice(0, -1).join("/");
    const newObj = { [fieldKey]: value };
    return {
      patches: [
        {
          op: "set",
          property: `/${parentPath}`,
          from: null,
          to: newObj,
        },
      ],
    };
  };

  /** Creates a patch object for updating the field */
  const createPatchObject = (
    field: string,
    currVal: string,
    value: string,
    parentPathExists: boolean
  ) => {
    const paths = field.split("/");
    const fieldKey = paths[paths.length - 1];

    return parentPathExists
      ? createPatchForExistingParent(field, currVal, value)
      : createPatchForNewParent(field, fieldKey, value);
  };

  /** Updates a nested object property with a given value. */
  const updateNestedObject = (obj: any, paths: string[], value: string) => {
    let nestedObj = obj;
    for (let i = 0; i < paths.length - 1; i++) {
      const currentPath = paths[i];
      if (
        !nestedObj[currentPath] ||
        typeof nestedObj[currentPath] !== "object"
      ) {
        nestedObj[currentPath] = {};
      }
      nestedObj = nestedObj[currentPath];
    }
    nestedObj[paths[paths.length - 1]] = value;
  };

  /**  Method to handle Editable fields for Date and Text changes */
  const handleChange = useCallback(
    async (
      id: string,
      field: string,
      value: string,
      params: GridCellParams,
      method: any
    ) => {
      const oldRow = { ...params.row };
      const paths = field.split("/");
      const fieldKey = paths[paths.length - 1];
      const currVal = params.formattedValue as string;
      let parentObj = params.row;
      let parentPathExists = true;


      // Iterate through the path to locate the parent object
      for (let i = 0; i < paths.length - 1; i++) {
        const currentPath = paths[i];
        if (
          !parentObj[currentPath] ||
          typeof parentObj[currentPath] !== "object"
        ) {
          parentObj[currentPath] = {};
          parentPathExists = false;
        }
        parentObj = parentObj[currentPath];
      }

      if (value !== currVal) {
        //create patch object
        const patchObject = createPatchObject(
          field,
          currVal,
          value,
          parentPathExists
        );
        parentObj[fieldKey] = value;
        try {
          await method({
            divisionCode: params.row.divisionCode,
            homesiteId: params.row.homeSiteId,
            body: patchObject,
          });
          params.api.updateRows([params.row]);
        } catch (apiError) {
          // if API fails revert back to original values.
          const rowObject = { ...oldRow };
          updateNestedObject(rowObject, paths, currVal);
          params.api.updateRows([rowObject]);
        }
      }
    },
    []
  );

  /** Function to Edit date fields. This will call the handleChange function to handle the changes.*/

  const handleChangeDate: any = useCallback(
    async (
      id: string,
      field: string,
      value: string,
      params: GridCellParams
    ) => {
      return handleChange(id, field, value, params, patchHomesite);
    },
    [handleChange, patchHomesite]
  );

  /** Function specifically for handling changes to the New Home Celebration Date/Time fields */
  const handleNHCDateTime = useCallback(
    async (
      id: string,
      field: string,
      value: Dayjs | null,
      params: GridCellParams
    ) => {
      if (value && !value.isValid()) return;


      const patchRequest = buildNHCDateTimePatch(
        params.row.NHCDate ?? null,
        params.row.NHCTime ?? null,
        value
      );
      const oldRow = { ...params.row };
      try {
        await patchHomesite({
          divisionCode: params.row.divisionCode,
          homesiteId: params.row.homeSiteId,
          body: patchRequest,
        });
        params.api.updateRows([params.row]);
      } catch (error) {
        params.api.updateRows([oldRow]);
      }
    },
    [patchHomesite]
  );

  const handleDateTimeChange = useCallback(async (
    id: string,
    field: string,
    value: Dayjs | null,
    params: GridCellParams
  ) => {
    if (value && !value.isValid()) {
      return;
    }
    // Convert Dayjs | null to string string)
    const valueStr = value ? value.format("YYYY-MM-DD HH:mm") : "";
    return handleChange(id, field, valueStr, params, patchHomesite);
  }, [handleChange, patchHomesite]);

  /** Function to Edit Text fields.This will call the handleChange function to handle the changes.*/

  const handleChangeText = useCallback(
    async (
      id: string,
      field: string,
      value: string,
      params: GridCellParams
    ) => {
      return handleChange(id, field, value, params, patchHomesite);
    },
    [handleChange, patchHomesite]
  );

  const renderHeaderWithText = (text: string) => {
    return () => (
      <div style={{ display: "flex", alignItems: "center" }}>
        <EditIcon
          style={{ color: "#005DAA", fontSize: "18px", marginRight: "4px" }}
        />
        <Tooltip title={text}>
          <span style={{ fontWeight: "bold" }}>{text}</span>
        </Tooltip>
      </div>
    );
  };

  /**
   * Pre-processes the edit cell props to validate the phone number.
   * @param {GridPreProcessEditCellProps} params - The parameters containing the cell props.
   * @returns {object} The updated cell props with an error message if the phone number is invalid.
   */
  const preProcessEditPhoneProps = useCallback(
    (params: GridPreProcessEditCellProps) => {
      const phoneValidatorResp = phoneValidator(params.props.value!.toString());
      return {
        ...params.props,
        error: phoneValidatorResp.isValid
          ? null
          : phoneValidatorResp.errorMessage,
      };
    },
    []
  );

  /**
   * Pre-processes the edit cell props to validate the text fields.
   * @param {GridPreProcessEditCellProps} params - The parameters containing the cell props.
   * @returns {object} The updated cell props with an error message if the input text is invalid.
   */
  const preProcessStringValidateProps = useCallback(
    (params: GridPreProcessEditCellProps) => {
      const textValidatorResp = textLengthValidator(
        50,
        GENERAL_TEXT_FIELD_MAX_LIMIT
      )(params.props.value!.toString());
      return {
        ...params.props,
        error: textValidatorResp.isValid
          ? null
          : textValidatorResp.errorMessage,
      };
    },
    []
  );

  /* Pre-processes the note to self validation on a grid cell.*/
  const preProcessNoteToSelfValidateProps = useCallback(
    (params: GridPreProcessEditCellProps) => {
      const noteToSelfValidatorResp = textLengthValidator(
        100,
        NOTE_TO_SELF_MAX_LIMIT
      )(params.props.value!.toString());
      return {
        ...params.props,
        error: noteToSelfValidatorResp.isValid
          ? null
          : noteToSelfValidatorResp.errorMessage,
      };
    },
    []
  );

  /**
   * Pre-processes the email validation properties for a grid cell.
   * @param {GridPreProcessEditCellProps} params - The parameters containing the cell properties.
   * @returns {object} The updated cell properties with an error message if the email is invalid.
   */
  const preProcessEmailValidateProps = useCallback(
    (params: GridPreProcessEditCellProps) => {
      const emailValidatorResp = emailValidator(params.props.value!.toString());
      return {
        ...params.props,
        error: !emailValidatorResp.isValid
          ? emailValidatorResp.errorMessage
          : null,
      };
    },
    []
  );

  /**
   * Pre-processes the final sales price by removing the dollar sign and converting the value to a number.
   * If the value is not a valid number, it returns an error message.
   *
   * @param {GridPreProcessEditCellProps} params - The parameters containing the cell props.
   * @returns {object} The updated cell props with an error message if the sales price is invalid.
   */
  const preProcessFinalSalesPrice = useCallback(
    (params: GridPreProcessEditCellProps) => {
      const value = params.props.value!.toString();
      // const numericValue = Number(value);
      // // Checking if the value is a valid number and limited to 10 digits.
      // const isValid = !isNaN(numericValue) && value.length <= 10;
      // debugger;
      const currValidatorResp = currencyValidator(value);
      return {
        ...params.props,
        error: currValidatorResp.isValid
          ? null
          : currValidatorResp.errorMessage,
      };
    },
    []
  );

  // here's the Date constructor documentation I referred to - https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/Date#examples
  /**
   * ! JS Date constructor reliably parses only ISO 8601 (e.g. 2025-07-21T00:00:00.000Z)
   */
  const stableDateFormat = useCallback((date: string | Date | null | undefined): Date | null => {
    if (date == null) return null;
    if (date instanceof Date) return Number.isNaN(date.getTime()) ? null : date;
    const dateStr = typeof date === "string" ? date : String(date);
    const standardizeDate = dateStr.replace(" ", "T"); /* Handle edge case: standardize "2025-07-21 00:00:00.0000000" format. */
    const getDatePart = standardizeDate.split("T")[0]; /* Get only the date part */
    const dateObj = new Date(getDatePart + "T12:00:00"); /* Set default time to 12:00:00 to avoid timezone issues */
    return Number.isNaN(dateObj.getTime()) ? null : dateObj;
  }, []);

  const mortgageAndTitleColumnsFunc = (startOrder: number, props: any): HomesiteColDef[] => {
    return props.map((prop: any, index: number) => {
      const fieldKey = prop.field as keyof HomeSitesDataIndex;
      const isDate = prop.type === "date";
      const isEditable = prop.editable === true;

      return {
        field: prop.field,
        headerName: prop.headerName,
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: prop.type || "string",
        availableInTables: {
          [TableColumnMap.ReportFields]: startOrder + index,
          [TableColumnMap.TaskFields]: startOrder + index,
          [TableColumnMap.BuyerListingFields]: startOrder + index,
          [TableColumnMap.BuyerListingMortgageTitleFields]: startOrder + index,
        },
        valueGetter: (_: any, row: HomeSitesDataIndex) => {
          const value = row[fieldKey];
          if (!value) return isDate ? null : "";
          return isDate ? stableDateFormat(value as string) : `${value}`;
        },
        valueFormatter: isDate ? formatDate : undefined,
        filterOperators: isDate ? customDateOperators : undefined,
        editable: isEditable,
        renderHeader: isEditable ? renderHeaderWithText(prop.headerName) : undefined,
        preProcessEditCellProps: isEditable ? preProcessStringValidateProps : undefined,
        renderEditCell: isEditable
          ? (params: GridRenderEditCellParams) => (
              <BuyerListingGridEditText
                {...params}
                id={params.row.id}
                field={params.field}
                value={params.value}
                params={params}
                handleChangeText={handleChangeText}
              />
            )
          : undefined,
        valueValidator: isEditable ? stringValidator : undefined,
      };
    });
  };

  const mortgageAndTitleColumns = mortgageAndTitleColumnsFunc(95, [
    { field: "loanComment", headerName: "Mortgage Comments" },
    { field: "loanGradeSFMortgage", headerName: "Mtg Grade" },
    { field: "propertyAppraisalOrderDate", headerName: "Appraisal Ord Dt", type: "date" },
    { field: "propertyAppraisalReceiveDate", headerName: "Appraisal Rcvd Dt", type: "date" },
    { field: "cdApprovedDate", headerName: "CD Approved Dt", type: "date" },
    { field: "loanClosingDate", headerName: "Closed Date: Loan Closing Date", type: "date" },
    { field: "titleCompany", headerName: "Title Company" },
    { field: "revisedTitleCompany", headerName: "Revised Title Company", editable: true },
    { field: "escrowClosingDate", headerName: "Escrow Closing Dt", type: "date" },
    { field: "loanProcessorName", headerName: "Processor" },
    { field: "lennarTrackConsent", headerName: "Lennar Track Consent" },
    { field: "packageDeliveredToLender", headerName: "Package Delivered to Lender" },
    { field: "lenderDocsReceived", headerName: "Lender Docs Received" },
    { field: "homeToClose", headerName: "Home to Close" },
  ]);

  const homesiteColumns: HomesiteColDef[] = useMemo(() => {
    return [
      {
        field: "buyerName",
        headerName: "Buyer Name(s)",
        customFlex: {
          [TableColumnMap.BuyerListingFields]: 1,
          [TableColumnMap.BuyerListingHomeSiteFields]: 1,
        },
        customMinWidth: {
          [TableColumnMap.BuyerListingFields]: 150,
        },
        filterable: true,
        customHideable: {
          [TableColumnMap.BuyerListingFields]: false,
          [TableColumnMap.ReportFields]: true,
        },
        headerClassName: "super-app-theme--header",
        type: "string",
        pinnable: true,
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 2,
          [TableColumnMap.BuyerListingBuyerFields]: 2,
          [TableColumnMap.BuyerListingHomeSiteFields]: 2,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 2,
          [TableColumnMap.ReportFields]: 3,
          [TableColumnMap.TaskFields]: 6,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.displayVals.name ? `${row.displayVals.name}` : "";
        },
      },
      {
        field: "homeSiteId",
        headerName: "Homesite",
        customFlex: {
          [TableColumnMap.BuyerListingFields]: 1,
          [TableColumnMap.ConciergeAssignmentFields]: 1,
          [TableColumnMap.ReportFields]: 1,
          [TableColumnMap.TaskFields]: 1,
        },
        customMinWidth: {
          [TableColumnMap.BuyerListingFields]: 150,
          [TableColumnMap.ReportFields]: 150,
          [TableColumnMap.TaskFields]: 150,
        },
        filterable: true,
        type: "string",
        pinnable: false,
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 1,
          [TableColumnMap.BuyerListingBuyerFields]: 1,
          [TableColumnMap.BuyerListingHomeSiteFields]: 1,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 1,
          [TableColumnMap.ReportFields]: 1,
          [TableColumnMap.TaskFields]: 1,
          [TableColumnMap.ConciergeAssignmentFields]: 1,
          [TableColumnMap.BuyerListingOSLFields]: 1,
        },
        headerClassName: "super-app-theme--header",
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.homeSiteId ? `${row.homeSiteId}` : "";
        },
      },
      {
        field: "buyerAddress",
        headerName: "Buyer Address",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 21,
          [TableColumnMap.BuyerListingBuyerFields]: 3,
          [TableColumnMap.ReportFields]: 4,
          [TableColumnMap.TaskFields]: 24,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.buyers && row.buyers[0] && row.buyers[0].address
            ? `${row.buyers[0].address}`
            : "";
        },
      },
      {
        field: "buyerEmail",
        headerName: "Buyer Email(s)",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 11,
          [TableColumnMap.BuyerListingBuyerFields]: 4,
          [TableColumnMap.ReportFields]: 5,
          [TableColumnMap.TaskFields]: 10,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.displayVals.email ? `${row.displayVals.email}` : "";
        },
      },
      {
        field: "buyerPhone",
        headerName: "Buyer Phone Number(s)",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 12,
          [TableColumnMap.BuyerListingBuyerFields]: 5,
          [TableColumnMap.ReportFields]: 6,
          [TableColumnMap.TaskFields]: 11,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.displayVals.phone ? `${row.displayVals.phone}` : "";
        },
      },
      {
        field: "nhc",
        headerName: "NHC",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 18,
          [TableColumnMap.BuyerListingBuyerFields]: 7,
          [TableColumnMap.ReportFields]: 7,
          [TableColumnMap.TaskFields]: 16,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.nhc ? `${row.nhc}` : "";
        },
      },
      {
        field: "homesitePhase",
        headerName: "Homesite Phase",
        filterable: true,
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingHomeSiteFields]: 4,
          [TableColumnMap.BuyerListingFields]: 84,
          [TableColumnMap.ReportFields]: 9,
          [TableColumnMap.TaskFields]: 86,
        },
        headerClassName: "super-app-theme--header",
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.homesitePhase ? `${row.homesitePhase}` : "";
        },
      },
      {
        field: "homesiteAddress",
        headerName: "HS Address",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 10,
          [TableColumnMap.BuyerListingHomeSiteFields]: 5,
          [TableColumnMap.ReportFields]: 10,
          [TableColumnMap.TaskFields]: 20,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.homesiteAddress ? `${row.homesiteAddress}` : "";
        },
      },
      {
        field: "communityName",
        headerName: "Community Name",
        customFlex: {
          [TableColumnMap.ConciergeAssignmentFields]: 1,
          [TableColumnMap.ReportFields]: 1,
        },
        customMinWidth: {
          [TableColumnMap.ReportFields]: 150,
        },
        customWidth: {
          [TableColumnMap.BuyerListingFields]: 150,
          [TableColumnMap.TaskFields]: 150,
        },
        filterable: true,
        customHideable: {
          [TableColumnMap.BuyerListingFields]: true,
          [TableColumnMap.ReportFields]: false,
        },
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 3,
          [TableColumnMap.BuyerListingHomeSiteFields]: 3,
          [TableColumnMap.ReportFields]: 2,
          [TableColumnMap.TaskFields]: 5,
          [TableColumnMap.ConciergeAssignmentFields]: 2,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.communityName ? `${row.communityName}` : "";
        },
      },
      {
        field: "broadBandDescription",
        headerName: "Broadband",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        customFlex: {
          [TableColumnMap.ConciergeAssignmentFields]: 1,
          [TableColumnMap.ReportFields]: 1,
        },
        customMinWidth: {
          [TableColumnMap.ReportFields]: 150,
        },
        customWidth: {
          [TableColumnMap.BuyerListingFields]: 150,
          [TableColumnMap.TaskFields]: 150,
        },
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 3.5,
          [TableColumnMap.BuyerListingHomeSiteFields]: 3.5,
          [TableColumnMap.ReportFields]: 2.5,
          [TableColumnMap.TaskFields]: 5.5,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.broadBandDescription ? `${row.broadBandDescription}` : "";
        },
      },
      {
        field: "hasAccessToHomeownerExperience",
        headerName: "Lennar Acc. Web.",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 2.1,
          [TableColumnMap.BuyerListingBuyerFields]: 2.1,
          [TableColumnMap.ReportFields]: 3.2,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return (row.buyers && row.buyers[0])
            ? row.buyers[0].hasAccessToHomeownerExperience ?? "" // if field exists, display it else display empty string
            : ""; // Default fallback if row.buyers doesn't exist. 
        },
      },
      {
        field: "hasAccessToLennarAccountMobileApp",
        headerName: "Lennar Acc. Mobile.",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 2.2,
          [TableColumnMap.BuyerListingBuyerFields]: 2.3,
          [TableColumnMap.ReportFields]: 3.5,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return (row.buyers && row.buyers[0])
            ? row.buyers[0].hasAccessToLennarAccountMobileApp ?? "" // if field exists, display it else display empty string
            : ""; // Default fallback if row.buyers doesn't exist. 
        },
      },
      {
        field: "opportunityId",
        headerName: "Opportunity ID",
        customFlex: {
          [TableColumnMap.ConciergeAssignmentFields]: 1,
        },
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 4,
          [TableColumnMap.ReportFields]: 11,
          [TableColumnMap.TaskFields]: 7,
          [TableColumnMap.ConciergeAssignmentFields]: 4,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.opportunityId ? `${row.opportunityId}` : "";
        },
        renderCell: (params: any) => {
          // Base URL for Salesforce link
          const baseUrl = `${salesforceUrl}/${params.value}/view`;

          if (params.value) {
            return (
              <Link
                href={baseUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "primary.main" }}
                aria-label={`Open ${params.label} in a new tab`}
              >
                {params.value}
              </Link>
            );
          } else {
            return <span>{params.value}</span>;
          }
        },
      },

      {
        field: "saleDate",
        headerName: "Sale Date",
        customFlex: {
          [TableColumnMap.BuyerListingFields]: 0,
          [TableColumnMap.ConciergeAssignmentFields]: 1,
        },
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "date",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 7,
          [TableColumnMap.BuyerListingBuyerFields]: 9,
          [TableColumnMap.BuyerListingHomeSiteFields]: 12,
          [TableColumnMap.ReportFields]: 12,
          [TableColumnMap.TaskFields]: 8,
          [TableColumnMap.ConciergeAssignmentFields]: 3,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.saleDate ? (stableDateFormat(row.saleDate)) : null;
        },
        //TODO: handle special formatter/ filterOperator
        valueFormatter: formatDate,
        filterOperators: customDateOperators,
      },
      {
        field: "closeOfEscrowEstimated",
        headerName: "JDE Est. COE Date",
        filterable: true,
        type: "date",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 5,
          [TableColumnMap.BuyerListingBuyerFields]: 10,
          [TableColumnMap.ReportFields]: 13,
          [TableColumnMap.TaskFields]: 9,
        },
        headerClassName: "super-app-theme--header",
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.closeOfEscrowEstimated
            ? (stableDateFormat(row.closeOfEscrowEstimated))
            : null;
        },
        valueFormatter: formatDate,
        filterOperators: customDateOperators,
      },
      {
        field: "closeOfEscrowEstimatedMortgage",
        headerName: "Mortgage ECOE",
        filterable: true,
        type: "date",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 5.1,
          [TableColumnMap.BuyerListingBuyerFields]: 10.5,
          [TableColumnMap.ReportFields]: 13.5,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 95.5,
        },
        headerClassName: "super-app-theme--header",
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.closeOfEscrowEstimatedMortgage
            ? (stableDateFormat(row.closeOfEscrowEstimatedMortgage))
            : null;
        },
        cellClassName: (params: any) => (params.row as HomeSitesDataIndex).ecoeMismatch ? "ecoe-mismatch-cell" : "",
        valueFormatter: formatDate,
        filterOperators: customDateOperators,
      },
      {
        field: "revisedEcoe",
        headerName: "Revised ECOE",
        filterable: true,
        editable: true,
        type: "date",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 6,
          [TableColumnMap.BuyerListingBuyerFields]: 11,
          [TableColumnMap.ReportFields]: 14,
          [TableColumnMap.TaskFields]: 18,
        },
        headerClassName: "super-app-theme--header",
        renderHeader: renderHeaderWithText("Revised ECOE"),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.revisedEcoe
            ? (stableDateFormat(row.revisedEcoe))
            : null;
        },
        renderEditCell: (params: GridRenderEditCellParams) => (
          <RevisedEcoeEditCell
            {...params}
            handleChangeDates={handleChangeDate}
            handleChangeText={handleChangeText}
          />
        ),
        valueFormatter: formatDate,
        valueValidator: dateValidator,
        filterOperators: customDateOperators,
      },
      {
        field: "delayedReason",
        headerName: "Delayed Reason",
        filterable: true,
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 6.5,
          [TableColumnMap.BuyerListingBuyerFields]: 11.5  ,
          [TableColumnMap.ReportFields]: 14.2,
          [TableColumnMap.TaskFields]: 18.2,
        },
        headerClassName: "super-app-theme--header",
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.delayedReason ? `${row.delayedReason}` : "";
        },
      },
      {
        field: "lockedCoe",
        headerName: "Locked COE",
        flex: 1,
        minWidth: 150,
        filterable: true,
        editable: true,
        type: "date",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 6.5,
          [TableColumnMap.BuyerListingBuyerFields]: 11.5,
          [TableColumnMap.ReportFields]: 14.5,
          [TableColumnMap.TaskFields]: 18.5,
        },
        headerClassName: "super-app-theme--header",
        renderHeader: renderHeaderWithText("Locked COE"),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.lockedCoe
            ? (stableDateFormat(row.lockedCoe))
            : null;
        },
        renderEditCell: (params: GridRenderEditCellParams) => (
          <GridEditDateCell
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeDates={handleChangeDate}
          />
        ),
        valueFormatter: formatDate,
        valueValidator: dateValidator,
        filterOperators: customDateOperators,
      },
      {
        field: "closingAptTime",
        headerName: "Closing Apt Time",
        filterable: true,
        editable: false,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 23,
          [TableColumnMap.BuyerListingBuyerFields]: 12,
          [TableColumnMap.ReportFields]: 15,
          [TableColumnMap.TaskFields]: 25,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.closingAptTime ? `${row.closingAptTime}` : "";
        },
      },
      {
        field: "constructionManager",
        headerName: "Construction Manager",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 19,
          [TableColumnMap.BuyerListingBuyerFields]: 8,
          [TableColumnMap.ReportFields]: 17,
          [TableColumnMap.TaskFields]: 23,
        },
        editable: true,
        renderHeader: renderHeaderWithText("Construction Manager"),
        preProcessEditCellProps: preProcessStringValidateProps,
        renderEditCell: (params: GridRenderEditCellParams) => (
          <BuyerListingGridEditText
            id={params.row.id}
            field="constructionManager"
            value={params.value ? params.value.toString() : ""}
            handleChangeText={handleChangeText}
            params={params}
          />
        ),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.constructionManager ? `${row.constructionManager}` : "";
        },
        valueValidator: stringValidator,
      },
      {
        field: "conciergeName",
        headerName: "Closing Concierge",
        customFlex: {
          [TableColumnMap.ConciergeAssignmentFields]: 1,
        },
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 30,
          [TableColumnMap.ReportFields]: 18,
          [TableColumnMap.ConciergeAssignmentFields]: 5,
          [TableColumnMap.TaskFields]: 32,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.conciergeName ? `${row.conciergeName}` : "";
        },
      },
      {
        field: "trenchDate",
        headerName: "Trench Date",
        filterable: true,
        type: "date",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 32,
          [TableColumnMap.BuyerListingHomeSiteFields]: 13,
          [TableColumnMap.ReportFields]: 20,
          [TableColumnMap.TaskFields]: 34,
        },
        headerClassName: "super-app-theme--header",
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.trenchDate
            ? (stableDateFormat(row.trenchDate))
            : null;
        },
        valueFormatter: formatDate,
        filterOperators: customDateOperators,
      },
      {
        field: "planElev",
        headerName: "Plan Elevation",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.ReportFields]: 21,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.planElev ? `${row.planElev}` : "";
        },
      },
      {
        field: "intPkg",
        headerName: "Interior Package",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.ReportFields]: 22,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.intPkg ? `${row.intPkg}` : "";
        },
      },
      {
        field: "extPkg",
        headerName: "Exterior Package",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.ReportFields]: 23,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.extPkg ? `${row.extPkg}` : "";
        },
      },
      {
        field: "constructionComplete",
        headerName: "Construction Complete Date",
        filterable: true,
        type: "date",
        availableInTables: {
          [TableColumnMap.ReportFields]: 24,
        },
        headerClassName: "super-app-theme--header",
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.constructionComplete
            ? (stableDateFormat(row.constructionComplete))
            : null;
        },
        valueFormatter: formatDate,
        filterOperators: customDateOperators,
      },
      {
        field: "certOfOccupancy",
        headerName: "Certificate of Occupancy Date",
        filterable: true,
        type: "date",
        availableInTables: {
          [TableColumnMap.ReportFields]: 25,
        },
        headerClassName: "super-app-theme--header",
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.certOfOccupancy
            ? (stableDateFormat(row.certOfOccupancy))
            : null;
        },
        valueFormatter: formatDate,
        filterOperators: customDateOperators,
      },
      {
        field: "titleStatus",
        headerName: "Title Status",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.ReportFields]: 26,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.titleStatus ? `${row.titleStatus}` : "";
        },
      },
      {
        field: "closingDocsSent",
        headerName: "Docs Sent",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "date",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 71,
          [TableColumnMap.ReportFields]: 27,
          [TableColumnMap.TaskFields]: 73,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 110,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.closingDocsSent
            ? (stableDateFormat(row.closingDocsSent))
            : null;
        },
        valueFormatter: formatDate,
        filterOperators: customDateOperators,
      },
      {
        field: "planNumber",
        headerName: "Plan Type Number",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 33,
          [TableColumnMap.BuyerListingHomeSiteFields]: 10,
          [TableColumnMap.ReportFields]: 28,
          [TableColumnMap.TaskFields]: 35,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.planNumber ? `${row.planNumber}` : "";
        },
      },
      {
        field: "planDescription",
        headerName: "Plan Description",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 34,
          [TableColumnMap.BuyerListingHomeSiteFields]: 11,
          [TableColumnMap.ReportFields]: 29,
          [TableColumnMap.TaskFields]: 36,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.planDescription ? `${row.planDescription}` : "";
        },
      },
      {
        field: "currentConstructionPhase",
        headerName: "Current Construction Phase",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 20,
          [TableColumnMap.BuyerListingHomeSiteFields]: 16,
          [TableColumnMap.ReportFields]: 30,
          [TableColumnMap.TaskFields]: 12,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.currentConstructionPhase
            ? `${row.currentConstructionPhase}`
            : "";
        },
      },
      {
        field: "electricity",
        headerName: "Electric",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "date",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 35,
          [TableColumnMap.BuyerListingHomeSiteFields]: 6,
          [TableColumnMap.ReportFields]: 31,
          [TableColumnMap.TaskFields]: 37,
        },
        editable: true,
        renderHeader: renderHeaderWithText("Electric"),
        renderEditCell: (params: GridRenderEditCellParams) => (
          <GridEditDateCell
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeDates={handleChangeDate}
          />
        ),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.electricity
            ? (stableDateFormat(row.electricity))
            : null;
        },
        valueFormatter: formatDate,
        valueValidator: dateValidator,
        filterOperators: customDateOperators,
      },
      {
        field: "gas",
        headerName: "Gas",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "date",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 36,
          [TableColumnMap.BuyerListingHomeSiteFields]: 7,
          [TableColumnMap.ReportFields]: 32,
          [TableColumnMap.TaskFields]: 38,
        },
        editable: true,
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.gas ? (stableDateFormat(row.gas)) : null;
        },
        renderHeader: renderHeaderWithText("Gas"),
        renderEditCell: (params: GridRenderEditCellParams) => (
          <GridEditDateCell
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeDates={handleChangeDate}
          />
        ),
        valueFormatter: formatDate,
        valueValidator: dateValidator,
        filterOperators: customDateOperators,
      },
      {
        field: "water",
        headerName: "Water Meter",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "date",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 37,
          [TableColumnMap.BuyerListingHomeSiteFields]: 8,
          [TableColumnMap.TaskFields]: 39,
          [TableColumnMap.ReportFields]: 33,
        },
        editable: true,
        renderHeader: renderHeaderWithText("Water Meter"),
        renderEditCell: (params: GridRenderEditCellParams) => (
          <GridEditDateCell
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeDates={handleChangeDate}
          />
        ),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.water ? (stableDateFormat(row.water)) : null;
        },
        valueFormatter: formatDate,
        valueValidator: dateValidator,
        filterOperators: customDateOperators,
      },
      {
        field: "solarType",
        headerName: "Solar Type",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 38,
          [TableColumnMap.BuyerListingHomeSiteFields]: 9,
          [TableColumnMap.TaskFields]: 40,
          [TableColumnMap.ReportFields]: 34,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.solarType ? `${row.solarType}` : "";
        },
      },
      {
        field: "flooring",
        headerName: "Flooring",
        filterable: true,
        type: "date",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 39,
          [TableColumnMap.BuyerListingHomeSiteFields]: 15,
          [TableColumnMap.TaskFields]: 41,
          [TableColumnMap.ReportFields]: 35,
        },
        editable: true,
        headerClassName: "super-app-theme--header",
        renderHeader: renderHeaderWithText("Flooring"),
        renderEditCell: (params: GridRenderEditCellParams) => (
          <GridEditDateCell
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeDates={handleChangeDate}
          />
        ),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.flooring ? (stableDateFormat(row.flooring)) : null;
        },
        valueFormatter: formatDate,
        valueValidator: dateValidator,
        filterOperators: customDateOperators,
      },
      {
        field: "qaWalkDate",
        headerName: "QA Walk Date",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "date",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 24,
          [TableColumnMap.BuyerListingBuyerFields]: 13,
          [TableColumnMap.TaskFields]: 26,
          [TableColumnMap.ReportFields]: 36,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.qaWalkDate
            ? (stableDateFormat(row.qaWalkDate))
            : null;
        },
        valueFormatter: formatDate,
        filterOperators: customDateOperators,
      },
      {
        field: "qaManager",
        headerName: "Quality Assurance Manager",
        filterable: true,
        editable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 25,
          [TableColumnMap.BuyerListingBuyerFields]: 14,
          [TableColumnMap.TaskFields]: 27,
          [TableColumnMap.ReportFields]: 37,
        },
        renderHeader: renderHeaderWithText("Quality Assurance Manager"),
        preProcessEditCellProps: preProcessStringValidateProps,
        renderEditCell: (params: GridRenderEditCellParams) => (
          <BuyerListingGridEditText
            {...params}
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeText={handleChangeText}
          />
        ),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.qaManager ? `${row.qaManager}` : "";
        },
        valueValidator: stringValidator,
      },
      {
        field: "NHCDateTime",
        headerName: "New Home Celebration Date/Time",
        filterable: true,
        editable: true,
        headerClassName: "super-app-theme--header",
        type: "dateTime",
        renderHeader: renderHeaderWithText("New Home Celebration Date/Time"),
        renderEditCell: (params: GridRenderEditCellParams) => (
          <GridEditDateTimeCell
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleDateTime={handleNHCDateTime}
          />
        ),
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 8,
          [TableColumnMap.BuyerListingBuyerFields]: 15,
          [TableColumnMap.TaskFields]: 19,
          [TableColumnMap.ReportFields]: 38,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return getNHCDateTime(row.NHCDate, row.NHCTime);
        },
        valueFormatter: formatDateTime,
        valueValidator: dateTimeValidator,
        filterOperators: customDateOperators,
      },
      {
        field: "NewHomeAcceptanceDateTime",
        headerName: "New Home Acceptance Date/Time",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "dateTime",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 73,
          [TableColumnMap.BuyerListingBuyerFields]: 16,
          [TableColumnMap.TaskFields]: 75,
          [TableColumnMap.ReportFields]: 39,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return getNHCDateTime(row.NHADate, row.NHATime);
        },
        valueFormatter: formatDateTime,
        filterOperators: customDateOperators,
      },
      {
        field: "finalSignOff",
        headerName: "Final Sign Off",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "date",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 83,
          [TableColumnMap.BuyerListingBuyerFields]: 18,
          [TableColumnMap.TaskFields]: 85,
          [TableColumnMap.ReportFields]: 41,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.finalSignOff
            ? (stableDateFormat(row.finalSignOff))
            : null;
        },
        valueFormatter: formatDate,
        filterOperators: customDateOperators,
      },
      {
        field: "mortgageType",
        headerName: "Mortgage Type",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 41,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 3,
          [TableColumnMap.TaskFields]: 43,
          [TableColumnMap.ReportFields]: 42,
        },
        editable: true,
        renderHeader: renderHeaderWithText("Mortgage Type"),
        preProcessEditCellProps: preProcessStringValidateProps,
        renderEditCell: (params) => (
          <BuyerListingGridEditText
            {...params}
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeText={handleChangeText}
          />
        ),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.mortgageType ? `${row.mortgageType}` : "";
        },
        valueValidator: stringValidator,
      },
      {
        field: "loanType",
        headerName: "Loan Type",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 16,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 4,
          [TableColumnMap.ReportFields]: 43,
          [TableColumnMap.TaskFields]: 15,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.loanType ? `${row.loanType}` : "";
        },
      },
      {
        field: LOANFOREIGNNATIONALLOAN,
        headerName: "Foreign National Loan",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "singleSelect",
        valueOptions: BuyerListingOptionsProvider.getOptionsByField(
          "loanForeignNationalLoan"
        ),
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 42,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 5,
          [TableColumnMap.TaskFields]: 44,
          [TableColumnMap.ReportFields]: 44,
        },
        editable: true,
        renderHeader: renderHeaderWithText("Foreign National Loan"),
        renderEditCell: (params) => (
          <BuyerListingOption
            {...params}
            params={params}
            handleChangeText={handleChangeText}
            value={params.value.toString()}
          />
        ),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.loanForeignNationalLoan
            ? `${row.loanForeignNationalLoan}`
            : "";
        },
      },
      {
        field: "lenderName_JDE",
        headerName: "Lender Name (JDE)",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 13,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 6,
          [TableColumnMap.ReportFields]: 45,
          [TableColumnMap.TaskFields]: 13,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.lenderName_JDE ? `${row.lenderName_JDE}` : "";
        },
      },
      {
        field: "lenderName_SF",
        headerName: "Lender Name (SF)",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 14,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 7,
          [TableColumnMap.ReportFields]: 46,
          [TableColumnMap.TaskFields]: 14,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.lenderName_SF ? `${row.lenderName_SF}` : "";
        },
      },
      {
        field: "loanOfficerName",
        headerName: "Loan Officer",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 43,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 8,
          [TableColumnMap.TaskFields]: 45,
          [TableColumnMap.ReportFields]: 47,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.loanOfficerName ? `${row.loanOfficerName}` : "";
        },
      },
      {
        field: "loanOfficerEmail",
        headerName: "Loan Officer Email",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 44,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 9,
          [TableColumnMap.TaskFields]: 46,
          [TableColumnMap.ReportFields]: 48,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.loanOfficerEmail ? `${row.loanOfficerEmail}` : "";
        },
      },
      {
        field: "loanStatus",
        headerName: "Loan Current Status",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 45,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 10,
          [TableColumnMap.TaskFields]: 47,
          [TableColumnMap.ReportFields]: 49,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.loanStatus ? `${row.loanStatus}` : "";
        },
      },
      {
        field: "loanConditionalApproval",
        headerName: "Loan Conditional Approval Date",
        filterable: true,
        type: "date",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 46,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 11,
          [TableColumnMap.TaskFields]: 48,
          [TableColumnMap.ReportFields]: 50,
        },
        headerClassName: "super-app-theme--header",
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.loanConditionalApproval
            ? (stableDateFormat(row.loanConditionalApproval))
            : null;
        },
        valueFormatter: formatDate,
        filterOperators: customDateOperators,
      },
      {
        field: "loanApprovalSF",
        headerName: "Loan Approval Date",
        filterable: true,
        type: "date",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 47,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 12,
          [TableColumnMap.TaskFields]: 49,
          [TableColumnMap.ReportFields]: 51,
        },
        headerClassName: "super-app-theme--header",
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.loanApprovalSF
            ? (stableDateFormat(row.loanApprovalSF))
            : null;
        },
        valueFormatter: formatDate,
        filterOperators: customDateOperators,
      },
      {
        field: "loanSigning",
        headerName: "Actual Loan Signing Date",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "date",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 48,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 13,
          [TableColumnMap.TaskFields]: 50,
          [TableColumnMap.ReportFields]: 52,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.loanSigning
            ? (stableDateFormat(row.loanSigning))
            : null;
        },
        valueFormatter: formatDate,
        filterOperators: customDateOperators,
      },
      {
        field: OSLLOANGRADE,
        headerName: "OSL Loan Grade",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "singleSelect",
        valueOptions:
          BuyerListingOptionsProvider.getOptionsByField(OSLLOANGRADE),
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 49,
          [TableColumnMap.BuyerListingOSLFields]: 2,
          [TableColumnMap.TaskFields]: 51,
          [TableColumnMap.ReportFields]: 53,
        },
        editable: true,
        renderHeader: renderHeaderWithText("OSL Loan Grade"),
        renderEditCell: (params) => (
          <BuyerListingOption
            {...params}
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeText={handleChangeText}
          />
        ),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.oslLoanGrade ? `${row.oslLoanGrade}` : null;
        },
      },
      {
        field: "osls/0/name",
        headerName: "OSL1: Name",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 50,
          [TableColumnMap.BuyerListingOSLFields]: 3,
          [TableColumnMap.TaskFields]: 52,
          [TableColumnMap.ReportFields]: 54,
        },
        editable: true,
        renderHeader: renderHeaderWithText("OSL1: Name"),
        preProcessEditCellProps: preProcessStringValidateProps,
        renderEditCell: (params: GridRenderEditCellParams) => (
          <BuyerListingGridEditText
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeText={handleChangeText}
          />
        ),
        valueValidator: stringValidator,
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.osls?.[0]?.name ? `${row.osls?.[0]?.name}` : "";
        },
      },
      {
        field: "osls/0/email",
        headerName: "OSL1: Email",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 51,
          [TableColumnMap.BuyerListingOSLFields]: 4,
          [TableColumnMap.TaskFields]: 53,
          [TableColumnMap.ReportFields]: 55,
        },
        editable: true,
        preProcessEditCellProps: preProcessEmailValidateProps,
        renderHeader: renderHeaderWithText("OSL1: Email"),
        renderEditCell: (params: GridRenderEditCellParams) => (
          <BuyerListingGridEditText
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeText={handleChangeText}
          />
        ),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.osls?.[0]?.email ? `${row.osls?.[0]?.email}` : "";
        },
        valueValidator: emailValidator,
      },
      {
        field: "osls/0/phone",
        headerName: "OSL1: Phone",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 52,
          [TableColumnMap.BuyerListingOSLFields]: 5,
          [TableColumnMap.TaskFields]: 54,
          [TableColumnMap.ReportFields]: 56,
        },
        editable: true,
        preProcessEditCellProps: preProcessEditPhoneProps,
        renderHeader: renderHeaderWithText("OSL1: Phone"),
        renderEditCell: (params: GridRenderEditCellParams) => (
          <BuyerListingGridEditText
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeText={handleChangeText}
          />
        ),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.osls?.[0]?.phone ? `${row.osls?.[0]?.phone}` : "";
        },
        valueValidator: phoneValidator,
      },
      {
        field: "osls/0/lockExpiery",
        headerName: "OSL1: Rate Lock Expiration Date",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "date",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 53,
          [TableColumnMap.BuyerListingOSLFields]: 6,
          [TableColumnMap.TaskFields]: 55,
          [TableColumnMap.ReportFields]: 57,
        },
        editable: true,
        renderHeader: renderHeaderWithText("OSL1: Rate Lock Expiration Date"),
        renderEditCell: (params: GridRenderEditCellParams) => (
          <GridEditDateCell
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeDates={handleChangeDate}
          />
        ),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.osls?.[0]?.lockExpiery
            ? (stableDateFormat(row.osls?.[0]?.lockExpiery))
            : null;
        },
        valueFormatter: formatDate,
        valueValidator: dateValidator,
        filterOperators: customDateOperators,
      },
      {
        field: "osls/0/type",
        headerName: "OSL1: Loan Type",

        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 54,
          [TableColumnMap.BuyerListingOSLFields]: 7,
          [TableColumnMap.TaskFields]: 56,
          [TableColumnMap.ReportFields]: 58,
        },
        editable: true,
        renderHeader: renderHeaderWithText("OSL1: Loan Type"),
        preProcessEditCellProps: preProcessStringValidateProps,
        renderEditCell: (params: GridRenderEditCellParams) => (
          <BuyerListingGridEditText
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeText={handleChangeText}
          />
        ),
        valueValidator: stringValidator,
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.osls?.[0]?.type ? `${row.osls?.[0]?.type}` : "";
        },
      },
      {
        field: "osls/0/status",
        headerName: "OSL1: Loan Current Status",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 55,
          [TableColumnMap.BuyerListingOSLFields]: 8,
          [TableColumnMap.TaskFields]: 57,
          [TableColumnMap.ReportFields]: 59,
        },
        editable: true,
        renderHeader: renderHeaderWithText("OSL1: Loan Current Status"),
        preProcessEditCellProps: preProcessStringValidateProps,
        renderEditCell: (params: GridRenderEditCellParams) => (
          <BuyerListingGridEditText
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeText={handleChangeText}
          />
        ),
        valueValidator: stringValidator,
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.osls?.[0]?.status ? `${row.osls?.[0]?.status}` : "";
        },
      },
      {
        field: "osls/0/condApproval",
        headerName: "OSL1: Conditional Loan Approval Date",
        filterable: true,
        type: "date",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 56,
          [TableColumnMap.BuyerListingOSLFields]: 9,
          [TableColumnMap.TaskFields]: 58,
          [TableColumnMap.ReportFields]: 60,
        },
        editable: true,
        headerClassName: "super-app-theme--header",
        renderHeader: renderHeaderWithText(
          "OSL1: Conditional Loan Approval Date"
        ),
        renderEditCell: (params: GridRenderEditCellParams) => (
          <GridEditDateCell
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeDates={handleChangeDate}
          />
        ),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.osls?.[0]?.condApproval
            ? (stableDateFormat(row.osls?.[0]?.condApproval))
            : null;
        },
        valueFormatter: formatDate,
        valueValidator: dateValidator,
        filterOperators: customDateOperators,
      },
      {
        field: "osls/0/approval",
        headerName: "OSL1: Loan Approval Date",
        filterable: true,
        type: "date",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 57,
          [TableColumnMap.BuyerListingOSLFields]: 10,
          [TableColumnMap.TaskFields]: 59,
          [TableColumnMap.ReportFields]: 61,
        },
        editable: true,
        headerClassName: "super-app-theme--header",
        renderHeader: renderHeaderWithText("OSL1: Loan Approval Date"),
        renderEditCell: (params: GridRenderEditCellParams) => (
          <GridEditDateCell
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeDates={handleChangeDate}
          />
        ),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.osls?.[0]?.approval
            ? (stableDateFormat(row.osls?.[0]?.approval))
            : null;
        },
        valueFormatter: formatDate,
        valueValidator: dateValidator,
        filterOperators: customDateOperators,
      },
      {
        field: "osls/1/name",
        headerName: "OSL2: Name",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 58,
          [TableColumnMap.BuyerListingOSLFields]: 11,
          [TableColumnMap.TaskFields]: 60,
          [TableColumnMap.ReportFields]: 62,
        },
        editable: true,
        renderHeader: renderHeaderWithText("OSL2: Name"),
        preProcessEditCellProps: preProcessStringValidateProps,
        renderEditCell: (params: GridRenderEditCellParams) => (
          <BuyerListingGridEditText
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeText={handleChangeText}
          />
        ),
        valueValidator: stringValidator,
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.osls?.[1]?.name ? `${row.osls?.[1]?.name}` : "";
        },
      },
      {
        field: "osls/1/email",
        headerName: "OSL2: Email",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 59,
          [TableColumnMap.BuyerListingOSLFields]: 12,
          [TableColumnMap.TaskFields]: 61,
          [TableColumnMap.ReportFields]: 63,
        },
        editable: true,
        preProcessEditCellProps: preProcessEmailValidateProps,
        renderHeader: renderHeaderWithText("OSL2: Email"),
        renderEditCell: (params: GridRenderEditCellParams) => (
          <BuyerListingGridEditText
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeText={handleChangeText}
          />
        ),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.osls?.[1]?.email ? `${row.osls?.[1]?.email}` : "";
        },
        valueValidator: emailValidator,
      },
      {
        field: "osls/1/phone",
        headerName: "OSL2: Phone",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 60,
          [TableColumnMap.BuyerListingOSLFields]: 13,
          [TableColumnMap.TaskFields]: 62,
          [TableColumnMap.ReportFields]: 64,
        },
        editable: true,
        preProcessEditCellProps: preProcessEditPhoneProps,
        renderHeader: renderHeaderWithText("OSL2: Phone"),
        renderEditCell: (params: GridRenderEditCellParams) => (
          <BuyerListingGridEditText
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeText={handleChangeText}
          />
        ),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.osls?.[1]?.phone ? `${row.osls?.[1]?.phone}` : "";
        },
        valueValidator: phoneValidator,
      },
      {
        field: "osls/1/lockExpiery",
        headerName: "OSL2: Rate Lock Expiration Date",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "date",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 61,
          [TableColumnMap.BuyerListingOSLFields]: 14,
          [TableColumnMap.TaskFields]: 63,
          [TableColumnMap.ReportFields]: 65,
        },
        editable: true,
        renderHeader: renderHeaderWithText("OSL2: Rate Lock Expiration Date"),
        renderEditCell: (params: GridRenderEditCellParams) => (
          <GridEditDateCell
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeDates={handleChangeDate}
          />
        ),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.osls?.[1]?.lockExpiery
            ? (stableDateFormat(row.osls?.[1]?.lockExpiery))
            : null;
        },
        valueFormatter: formatDate,
        valueValidator: dateValidator,
        filterOperators: customDateOperators,
      },
      {
        field: "osls/1/type",
        headerName: "OSL2: Loan Type",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 62,
          [TableColumnMap.BuyerListingOSLFields]: 15,
          [TableColumnMap.TaskFields]: 64,
          [TableColumnMap.ReportFields]: 66,
        },
        editable: true,
        renderHeader: renderHeaderWithText("OSL2: Loan Type"),
        preProcessEditCellProps: preProcessStringValidateProps,
        renderEditCell: (params: GridRenderEditCellParams) => (
          <BuyerListingGridEditText
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeText={handleChangeText}
          />
        ),
        valueValidator: stringValidator,
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.osls?.[1]?.type ? `${row.osls?.[1]?.type}` : "";
        },
      },
      {
        field: "osls/1/status",
        headerName: "OSL2: Loan Current Status",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 63,
          [TableColumnMap.BuyerListingOSLFields]: 16,
          [TableColumnMap.TaskFields]: 65,
          [TableColumnMap.ReportFields]: 67,
        },
        editable: true,
        renderHeader: renderHeaderWithText("OSL2: Loan Current Status"),
        preProcessEditCellProps: preProcessStringValidateProps,
        renderEditCell: (params: GridRenderEditCellParams) => (
          <BuyerListingGridEditText
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeText={handleChangeText}
          />
        ),
        valueValidator: stringValidator,
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.osls?.[1]?.status ? `${row.osls?.[1]?.status}` : "";
        },
      },
      {
        field: "osls/1/condApproval",
        headerName: "OSL2: Conditional Loan Approval Date",
        filterable: true,
        type: "date",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 64,
          [TableColumnMap.BuyerListingOSLFields]: 17,
          [TableColumnMap.TaskFields]: 66,
          [TableColumnMap.ReportFields]: 68,
        },
        editable: true,
        headerClassName: "super-app-theme--header",
        renderHeader: renderHeaderWithText(
          "OSL2: Conditional Loan Approval Date"
        ),
        renderEditCell: (params: GridRenderEditCellParams) => (
          <GridEditDateCell
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeDates={handleChangeDate}
          />
        ),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.osls?.[1]?.condApproval
            ? (stableDateFormat(row.osls?.[1]?.condApproval))
            : null;
        },
        valueFormatter: formatDate,
        valueValidator: dateValidator,
        filterOperators: customDateOperators,
      },
      {
        field: "osls/1/approval",
        headerName: "OSL2: Loan Approval Date",
        filterable: true,
        type: "date",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 65,
          [TableColumnMap.BuyerListingOSLFields]: 18,
          [TableColumnMap.TaskFields]: 67,
          [TableColumnMap.ReportFields]: 69,
        },
        editable: true,
        headerClassName: "super-app-theme--header",
        renderHeader: renderHeaderWithText("OSL2: Loan Approval Date"),
        renderEditCell: (params: GridRenderEditCellParams) => (
          <GridEditDateCell
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeDates={handleChangeDate}
          />
        ),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.osls?.[1]?.approval
            ? (stableDateFormat(row.osls?.[1]?.approval))
            : null;
        },
        valueFormatter: formatDate,
        valueValidator: dateValidator,
        filterOperators: customDateOperators,
      },
      {
        field: "ostName",
        headerName: "OST: Name",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 65.1,
          [TableColumnMap.BuyerListingOSLFields]: 19,
          [TableColumnMap.TaskFields]: 67.1,
          [TableColumnMap.ReportFields]: 69.1,
        },
        editable: true,
        renderHeader: renderHeaderWithText("OST: Name"),
        preProcessEditCellProps: preProcessStringValidateProps,
        renderEditCell: (params: GridRenderEditCellParams) => (
          <BuyerListingGridEditText
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeText={handleChangeText}
          />
        ),
        valueValidator: stringValidator,
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.ostName ? `${row.ostName}` : "";
        },
      },
      {
        field: "ostEmail",
        headerName: "OST: Email",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 65.2,
          [TableColumnMap.BuyerListingOSLFields]: 20,
          [TableColumnMap.TaskFields]: 67.2,
          [TableColumnMap.ReportFields]: 69.2,
        },
        editable: true,
        preProcessEditCellProps: preProcessEmailValidateProps,
        renderHeader: renderHeaderWithText("OST: Email"),
        renderEditCell: (params: GridRenderEditCellParams) => (
          <BuyerListingGridEditText
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeText={handleChangeText}
          />
        ),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.ostEmail ? `${row.ostEmail}` : "";
        },
        valueValidator: emailValidator,
      },
      {
        field: "loanGrade",
        headerName: "Lennar Loan Grade",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 66,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 31,
          [TableColumnMap.TaskFields]: 68,
          [TableColumnMap.ReportFields]: 70,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.loanGrade ? `${row.loanGrade}` : "";
        },
      },
      {
        field: "loanDeclined",
        headerName: "Declined By Lennar",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 67,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 35,
          [TableColumnMap.TaskFields]: 69,
          [TableColumnMap.ReportFields]: 71,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.loanDeclined ? `${row.loanDeclined}` : "";
        },
      },
      {
        field: "contingent",
        headerName: "Contingent",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 68,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 36,
          [TableColumnMap.TaskFields]: 70,
          [TableColumnMap.ReportFields]: 72,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.contingent === true
            ? "True"
            : row.contingent === false
              ? "False"
              : "";
        },
      },
      {
        field: "contingentType",
        headerName: "Contingency Type",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 15,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 37,
          [TableColumnMap.TaskFields]: 21,
          [TableColumnMap.ReportFields]: 73,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.contingentType ? `${row.contingentType}` : "";
        },
      },
      {
        field: "homeToCloseDate",
        headerName: "Home To Close Date",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "date",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 69,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 107,
          [TableColumnMap.TaskFields]: 71,
          [TableColumnMap.ReportFields]: 74,
        },
        editable: true,
        renderHeader: renderHeaderWithText("Home To Close Date"),
        renderEditCell: (params: GridRenderEditCellParams) => (
          <GridEditDateCell
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeDates={handleChangeDate}
          />
        ),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row?.homeToCloseDate
            ? (stableDateFormat(row?.homeToCloseDate))
            : null;
        },
        valueFormatter: formatDate,
        valueValidator: dateValidator,
        filterOperators: customDateOperators,
      },
      {
        field: "loanLockExpirationDate",
        headerName: "Lock Expiration Date",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "date",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 70,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 39,
          [TableColumnMap.TaskFields]: 72,
          [TableColumnMap.ReportFields]: 75,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.loanLockExpirationDate
            ? (stableDateFormat(row.loanLockExpirationDate))
            : null;
        },
        valueFormatter: formatDate,
        valueValidator: dateValidator,
        filterOperators: customDateOperators,
      },
      {
        field: "estimatedDeliverableDate",
        headerName: "Estimated Deliverable Date",
        filterable: true,
        type: "date",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 40,
          [TableColumnMap.BuyerListingHomeSiteFields]: 14,
          [TableColumnMap.TaskFields]: 42,
          [TableColumnMap.ReportFields]: 76,
        },
        headerClassName: "super-app-theme--header",
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.estimatedDeliverableDate
            ? (stableDateFormat(row.estimatedDeliverableDate))
            : null;
        },
        valueFormatter: formatDate,
        filterOperators: customDateOperators,
      },
      {
        field: "totalOptions",
        headerName: "Total Options",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 73,
          [TableColumnMap.BuyerListingBuyerFields]: 19,
          [TableColumnMap.TaskFields]: 75,
          [TableColumnMap.ReportFields]: 77,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.totalOptions ? `${row.totalOptions}` : "";
        },
        valueFormatter: formatCurrency,
      },
      {
        field: "optionIncentive",
        headerName: "Option Incentive",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 74,
          [TableColumnMap.BuyerListingBuyerFields]: 20,
          [TableColumnMap.TaskFields]: 76,
          [TableColumnMap.ReportFields]: 78,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.optionIncentive ? `${row.optionIncentive}` : "";
        },
        valueFormatter: formatCurrency,
      },
      {
        field: "totalPrice",
        headerName: "Total Price",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 75,
          [TableColumnMap.BuyerListingBuyerFields]: 21,
          [TableColumnMap.TaskFields]: 77,
          [TableColumnMap.ReportFields]: 79,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.totalPrice ? `${row.totalPrice}` : "";
        },
        valueFormatter: formatCurrency,
      },
      {
        field: "paymentsCollected",
        headerName: "Payments Collected",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 76,
          [TableColumnMap.BuyerListingBuyerFields]: 22,
          [TableColumnMap.TaskFields]: 78,
          [TableColumnMap.ReportFields]: 80,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.paymentsCollected ? `${row.paymentsCollected}` : "";
        },
        valueFormatter: formatCurrency,
      },
      {
        field: "paymentsRemaining",
        headerName: "Payments Remaining",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 77,
          [TableColumnMap.BuyerListingBuyerFields]: 23,
          [TableColumnMap.TaskFields]: 79,
          [TableColumnMap.ReportFields]: 81,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.paymentsRemaining ? `${row.paymentsRemaining}` : "";
        },
        valueFormatter: formatCurrency,
      },
      {
        field: "revenueSubtotal",
        headerName: "Revenue Subtotal",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 78,
          [TableColumnMap.BuyerListingBuyerFields]: 24,
          [TableColumnMap.TaskFields]: 80,
          [TableColumnMap.ReportFields]: 82,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.revenueSubtotal ? `${row.revenueSubtotal}` : "";
        },
        valueFormatter: formatCurrency,
      },
      {
        field: "finalSalesPrice",
        headerName: "Final Sales Price",
        filterable: true,
        editable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 79,
          [TableColumnMap.BuyerListingBuyerFields]: 25,
          [TableColumnMap.TaskFields]: 81,
          [TableColumnMap.ReportFields]: 83,
        },
        preProcessEditCellProps: preProcessFinalSalesPrice,
        renderHeader: renderHeaderWithText("Final Sales Price"),
        renderEditCell: (params) => {
          return (
            <BuyerListingGridEditText
              {...params}
              id={params.row.id}
              field={params.field}
              value={params.value}
              params={params}
              handleChangeText={handleChangeText}
            />
          );
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.finalSalesPrice ? `${row.finalSalesPrice}` : "";
        },
        valueFormatter: formatCurrency,
        valueValidator: currencyValidator,
      },
      {
        field: "preConstruction",
        headerName: "Pre-Construction Date/Time",
        filterable: true,
        editable: true,
        headerClassName: "super-app-theme--header",
        type: "dateTime",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 80,
          [TableColumnMap.BuyerListingBuyerFields]: 26,
          [TableColumnMap.TaskFields]: 82,
          [TableColumnMap.ReportFields]: 84,
        },

        renderHeader: renderHeaderWithText("Pre-Construction Date/Time"),
        renderEditCell: (params: GridRenderEditCellParams) => (
          <GridEditDateTimeCell
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleDateTime={handleDateTimeChange}
          />
        ),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.preConstruction ? new Date(row.preConstruction) : null;
        },
        valueFormatter: formatDateTime,
        valueValidator: dateTimeValidator,
        filterOperators: customDateOperators,
      },
      {
        field: "preDrywall",
        headerName: "Pre-Drywall Date/Time",
        filterable: true,
        editable: true,
        headerClassName: "super-app-theme--header",
        type: "dateTime",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 81,
          [TableColumnMap.BuyerListingBuyerFields]: 27,
          [TableColumnMap.TaskFields]: 83,
          [TableColumnMap.ReportFields]: 85,
        },
        renderHeader: renderHeaderWithText("Pre-Drywall Date/Time"),
        renderEditCell: (params: GridRenderEditCellParams) => (
          <GridEditDateTimeCell
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleDateTime={handleDateTimeChange}
          />
        ),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.preDrywall ? new Date(row.preDrywall) : null;
        },
        valueFormatter: formatDateTime,
        valueValidator: dateTimeValidator,
        filterOperators: customDateOperators,
      },
      {
        field: "lastContact",
        headerName: "Last Date of Contact",
        filterable: true,
        editable: true,
        headerClassName: "super-app-theme--header",
        type: "date",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 26,
          [TableColumnMap.BuyerListingBuyerFields]: 28,
          [TableColumnMap.TaskFields]: 28,
          [TableColumnMap.ReportFields]: 86,
        },
        renderHeader: renderHeaderWithText("Last Date of Contact"),
        renderEditCell: (params: GridRenderEditCellParams) => (
          <GridEditDateCell
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeDates={handleChangeDate}
            disableFuture
          />
        ),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.lastContact
            ? (stableDateFormat(row.lastContact))
            : null;
        },
        valueFormatter: formatDate,
        valueValidator: contactDateValidator,
        filterOperators: customDateOperators,
      },
      {
        field: CONTACTMETHOD,
        headerName: "Method of Contact",
        filterable: true,
        editable: true,
        headerClassName: "super-app-theme--header",
        type: "singleSelect",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 27,
          [TableColumnMap.BuyerListingBuyerFields]: 29,
          [TableColumnMap.TaskFields]: 29,
          [TableColumnMap.ReportFields]: 87,
        },
        valueOptions:
          BuyerListingOptionsProvider.getOptionsByField(CONTACTMETHOD),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.contactMethod ? `${row.contactMethod}` : null;
        },
        renderHeader: renderHeaderWithText("Method of Contact"),
        renderEditCell: (params) => (
          <BuyerListingOption
            {...params}
            params={params}
            handleChangeText={handleChangeText}
            value={params.value}
          />
        ),
      },
      {
        field: "closeOfEscrowActual",
        headerName: "Actual Close of Escrow",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "date",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 82,
          [TableColumnMap.BuyerListingBuyerFields]: 30,
          [TableColumnMap.TaskFields]: 84,
          [TableColumnMap.ReportFields]: 88,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row?.closeOfEscrowActual
            ? (stableDateFormat(row?.closeOfEscrowActual))
            : null;
        },
        valueFormatter: formatDate,
        filterOperators: customDateOperators,
      },
      {
        field: "escrowOfficer",
        headerName: "Escrow Officer",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 82.5,
          [TableColumnMap.BuyerListingBuyerFields]: 31,
          [TableColumnMap.TaskFields]: 84.5,
          [TableColumnMap.ReportFields]: 88.5,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 104,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.escrowOfficer ? `${row.escrowOfficer}` : "";
        },
      },
      {
        field: "latestException",
        headerName: "Latest Exception",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.ReportFields]: 92,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.latestException ? `${row.latestException}` : "";
        },
      },
      {
        field: "latestNote",
        headerName: "Latest Note",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.ReportFields]: 93,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.latestNote ? `${row.latestNote}` : "";
        },
      },
      {
        field: "coBroker",
        headerName: "Co-Broker?",
        filterable: true,
        editable: false,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 17,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 32,
          [TableColumnMap.TaskFields]: 22,
          [TableColumnMap.ReportFields]: 91,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          if (row.coBroker === true) {
            return "Yes";
          } else if (row.coBroker === false) {
            return "No";
          }
          return "";
        },
      },
      {
        field: "realtorName",
        headerName: "Realtor Name",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 28,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 33,
          [TableColumnMap.TaskFields]: 30,
          [TableColumnMap.ReportFields]: 89,
          [TableColumnMap.BuyerListingBuyerFields]: 32,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.realtorName ? `${row.realtorName}` : "";
        },
      },
      {
        field: "realtorEmail",
        headerName: "Realtor Email",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 29,
          [TableColumnMap.ReportFields]: 90,
          [TableColumnMap.TaskFields]: 31,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 34,
          [TableColumnMap.BuyerListingBuyerFields]: 33,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.realtorEmail ? `${row.realtorEmail}` : "";
        },
      },
      {
        field: "homeToSell",
        headerName: "Home To Sell",
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 31,
          [TableColumnMap.ReportFields]: 91,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 35,
          [TableColumnMap.TaskFields]: 33,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.homeToSell ? `${row.homeToSell}` : "";
        },
      },
      {
        field: "milestoneName",
        headerName: "Milestone Name",
        customFlex: { [TableColumnMap.TaskFields]: 1 },
        customMinWidth: { [TableColumnMap.TaskFields]: 150 },
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        pinnable: false,
        availableInTables: {
          [TableColumnMap.TaskFields]: 2,
        },
        valueGetter: (value, row: MilestoneTask) => {
          return row.mileStoneName ? `${row.mileStoneName}` : "";
        },
        renderCell: (params: any) => (
          <div
            style={{
              cursor: "pointer",
            }}
          >
            {params.value}
          </div>
        ),
      },
      {
        field: "taskName",
        headerName: "Task Name",
        customFlex: { [TableColumnMap.TaskFields]: 1 },
        customMinWidth: { [TableColumnMap.TaskFields]: 150 },
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.TaskFields]: 3,
        },
        valueGetter: (value, row: MilestoneTask) => {
          return row.taskName ? `${row.taskName}` : "";
        },
      },
      {
        field: "taskDueDate",
        headerName: "Task Due Date",
        customFlex: { [TableColumnMap.TaskFields]: 1 },
        customMinWidth: { [TableColumnMap.TaskFields]: 150 },
        filterable: true,
        headerClassName: "super-app-theme--header",
        type: "date",
        availableInTables: {
          [TableColumnMap.TaskFields]: 4,
        },
        valueGetter: (value, row: MilestoneTask) => {
          return row.dueDate ? (new Date(row?.dueDate)) : null;
        },
        valueFormatter: formatDate,
      },
      {
        field: "comments",
        headerName: "Notes to Self",
        flex: 1,
        minWidth: 150,
        filterable: true,
        editable: true,
        preProcessEditCellProps: preProcessNoteToSelfValidateProps,
        renderHeader: renderHeaderWithText("Notes to Self"),
        headerClassName: "super-app-theme--header",
        type: "string",
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 88,
          [TableColumnMap.BuyerListingBuyerFields]: 33,
          [TableColumnMap.ReportFields]: 94,
          [TableColumnMap.TaskFields]: 87,
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.comments ? `${row.comments}` : "";
        },
        renderEditCell: (params: GridRenderEditCellParams) => (
          <BuyerListingGridEditText
            id={params.row.id}
            field={params.field}
            value={params.value}
            params={params}
            handleChangeText={handleChangeText}
          />
        ),
        valueValidator: noteToSelfValidator,
      },
      {
        field: "numExceptions",
        headerName: "Exceptions",
        flex: 1,
        minWidth: 40,
        maxWidth: 65,
        filterable: true,
        disableColumnMenu: false,
        sortable: true,
        type: "number",
        headerClassName: "super-app-theme--header",
        headerAlign: "center",
        align: "center",
        pinnable: false,
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 89,
          [TableColumnMap.BuyerListingBuyerFields]: 34,
          [TableColumnMap.BuyerListingHomeSiteFields]: 18,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 40,
          [TableColumnMap.TaskFields]: 88,
          [TableColumnMap.BuyerListingOSLFields]: 19,
        },
        renderHeader: () => (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Tooltip title="Exceptions">
              <WarningAmberIcon style={{ color: "#C93939", fontSize: "18px" }} />
            </Tooltip>
          </div>
        ),
        renderCell: (params: any) => (
          <div
            style={{
              cursor: "pointer",
            }}
          >
            {params.value}
          </div>
        ),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.numExceptions || 0;
        },
      },
      {
        field: "numNotes",
        headerName: "Notes",
        flex: 1,
        minWidth: 40,
        maxWidth: 65,
        filterable: true,
        disableColumnMenu: false,
        sortable: true,
        type: "number",
        headerClassName: "super-app-theme--header",
        headerAlign: "center",
        align: "center",
        pinnable: false,
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 91,
          [TableColumnMap.BuyerListingBuyerFields]: 36,
          [TableColumnMap.BuyerListingHomeSiteFields]: 20,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 42,
          [TableColumnMap.TaskFields]: 90,
          [TableColumnMap.BuyerListingOSLFields]: 20,
        },
        renderHeader: () => (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Tooltip title="Notes">
              <ChatBubbleOutlineIcon style={{ color: "#3F63C0", fontSize: "18px" }} />
            </Tooltip>
          </div>
        ),
        renderCell: (params: any) => {
          const notesData = params.row.notesData || [];
          const homeSiteId = params.row.homeSiteId;
          const hasNotes = notesData.length > 0;

          const tooltipContent = hasNotes ? (
            <NotesTooltip notes={notesData} homeSiteId={homeSiteId} />
          ) : null;

          return (
            <div
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
              }}
            >
              {tooltipContent ? (
                <Tooltip
                  title={tooltipContent}
                  arrow
                  placement="right"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: 'white',
                        color: 'text.primary',
                        border: '1px solid rgba(0, 0, 0, 0.12)',
                        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
                        p: 0,
                        maxWidth: 'none',
                      },
                    },
                    arrow: {
                      sx: {
                        color: 'white',
                        '&::before': {
                          border: '1px solid rgba(0, 0, 0, 0.12)',
                        },
                      },
                    },
                  }}
                >
                  <div style={{ cursor: 'pointer' }}>
                    {params.value}
                  </div>
                </Tooltip>
              ) : (
                <div>{params.value}</div>
              )}
            </div>
          );
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.numNotes || 0;
        },
      },
      {
        field: "numAlerts",
        headerName: "Alerts",
        flex: 1,
        minWidth: 40,
        maxWidth: 65,
        filterable: true,
        disableColumnMenu: false,
        sortable: true,
        type: "number",
        headerClassName: "super-app-theme--header",
        headerAlign: "center",
        align: "center",
        pinnable: false,
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 90,
          [TableColumnMap.BuyerListingBuyerFields]: 35,
          [TableColumnMap.BuyerListingHomeSiteFields]: 19,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 41,
          [TableColumnMap.TaskFields]: 89,
          [TableColumnMap.BuyerListingOSLFields]: 21,
        },
        renderHeader: () => (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Tooltip title="Alerts">
              <ErrorOutlineIcon style={{ color: "#C58026", fontSize: "18px" }} />
            </Tooltip>
          </div>
        ),
        renderCell: (params: any) => (
          <div
            style={{
              cursor: "pointer",
            }}
          >
            {params.value}
          </div>
        ),
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.numAlerts || 0;
        },
      },
      {
        field: "milestoneProgress",
        headerName: "Milestone Progress",
        flex: 1,
        minWidth: 50,
        maxWidth: 70,
        filterable: true,
        disableColumnMenu: false,
        sortable: true,
        type: "number",
        headerClassName: "super-app-theme--header",
        headerAlign: "center",
        align: "center",
        cellClassName: "milestone-progress-cell",
        pinnable: false,
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 91,
          [TableColumnMap.BuyerListingBuyerFields]: 36,
          [TableColumnMap.BuyerListingHomeSiteFields]: 20,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 42,
          [TableColumnMap.BuyerListingOSLFields]: 22,
        },
        renderHeader: () => (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Tooltip title="Milestone Progress">
              <DonutLargeIcon style={{ color: "#4caf50", fontSize: "18px" }} />
            </Tooltip>
          </div>
        ),
        renderCell: (params: any) => {
          return <MilestoneProgressCell params={params} />;
        },
        valueGetter: (value, row: HomeSitesDataIndex) => {
          return row.milestoneProgress || 0;
        },
      },
      {
        field: "contactInfo",
        headerName: "Contact Info",
        sortable: false,
        flex: 1,
        minWidth: 45,
        maxWidth: 80,
        hideable: false,
        filterable: false,
        headerClassName: "super-app-theme--header",
        disableColumnMenu: true,
        align: "center",
        headerAlign: "center",
        type: "string",
        pinnable: false,
        renderHeader: () => (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Tooltip title="Contact Info">
              <ContactsIcon style={{ color: "#1976d2", fontSize: "18px" }} />
            </Tooltip>
          </div>
        ),
        renderCell: (params: any) => <BuyerListingActions {...params} />,
        availableInTables: {
          [TableColumnMap.BuyerListingFields]: 93,
          [TableColumnMap.TaskFields]: 92,
          [TableColumnMap.BuyerListingBuyerFields]: 38,
          [TableColumnMap.BuyerListingHomeSiteFields]: 22,
          [TableColumnMap.BuyerListingMortgageTitleFields]: 44,
          [TableColumnMap.BuyerListingOSLFields]: 23,
        },
      },

      /* 
      TODO: Mortgage and Title Details. All additional columns go in here.
      */
      ...mortgageAndTitleColumns
    ];
  }, [
    handleChangeDate,
    handleChangeText,
    customDateOperators,
    preProcessEditPhoneProps,
    preProcessStringValidateProps,
    preProcessEmailValidateProps,
    preProcessFinalSalesPrice,
    handleNHCDateTime,
    preProcessNoteToSelfValidateProps,
    handleDateTimeChange,
    stableDateFormat,
    mortgageAndTitleColumns
  ]);

  /*
   * Generates a list of homesite grid columns based on the provided parameters.
   * Filters out hidden columns and adjusts properties based on editability and lock status.
   */
  const getHomesiteGridColumns = useCallback(
    (
      editable: boolean,
      locked: string[],
      location: TableColumnMap
    ): HomesiteColDef[] => {
      return homesiteColumns
        .filter(
          (col) => col.availableInTables && location in col.availableInTables
        )
        .filter(
          (col) => col.field !== "milestoneProgress" || showMilestoneProgress
        )
        .map((column) => {
          const isLocked = locked.includes(column.field);

          return {
            ...column,
            flex: column.customFlex?.[location],
            minWidth: column.customMinWidth?.[location],
            width:
              column.customWidth?.[location] ??
              150 /* ?? is called nullish coalescing. If the value on the left is null or undefined, it will return the value on the right. */,
            editable: editable && column.editable,
            renderEditCell:
              editable && column.editable ? column.renderEditCell : undefined,
            // Preserve renderHeader for all columns, only remove for editable columns when editing is disabled
            renderHeader:
              column.editable && !editable ? undefined : column.renderHeader,
            hideable: !isLocked,
          };
        });
    },
    [homesiteColumns, showMilestoneProgress]
  );

  /*
   * Returns a sorted array of grid columns based on their position in a specified table location.
   * Filters columns based on their availability in the specified location and sorts them by their defined order.
   */
  const getSortedColumns = useCallback(
    (
      editable: boolean,
      locked: string[],
      location: TableColumnMap
    ): HomesiteColDef[] => {
      const MAX_NUMBER_OF_COLUMNS_IN_CLO = 200; // assuming the CLO won't have more than 200 columns
      const homesiteColumns = getHomesiteGridColumns(
        editable,
        locked,
        location
      );

      const sortedColumns = homesiteColumns.sort((a, b) => {
        const orderA =
          a.availableInTables?.[location] ?? MAX_NUMBER_OF_COLUMNS_IN_CLO;
        const orderB =
          b.availableInTables?.[location] ?? MAX_NUMBER_OF_COLUMNS_IN_CLO;
        return orderA - orderB;
      });

      return sortedColumns;
    },
    [getHomesiteGridColumns]
  );

  const setBuyerlistingTables = useCallback(
    (tableColumnMap: TableColumnMap): HomesiteColDef[] => {
      const pinnedColumns = ["buyerName", "homeSiteId", "numExceptions", "numNotes", "numAlerts", "contactInfo"];
      if (showMilestoneProgress) {
        pinnedColumns.splice(5, 0, "milestoneProgress"); // Insert before contactInfo
      }
      const sortedColoumns = getSortedColumns(
        true,
        pinnedColumns,
        tableColumnMap
      );

      /*
       * Here, we are changing the color of Homesite to blue when the user hovers over it to indicate that it is clickable.
       * This is applied only on My View, Buyer, Homesite, and Lender Details tab.
       * NOT on Reports.
       */
      const hoverColorColumn = sortedColoumns.map((col) => {
        if (col.field === "homeSiteId") {
          if (
            tableColumnMap === TableColumnMap.BuyerListingFields ||
            tableColumnMap === TableColumnMap.BuyerListingBuyerFields ||
            tableColumnMap === TableColumnMap.BuyerListingHomeSiteFields ||
            tableColumnMap === TableColumnMap.BuyerListingMortgageTitleFields ||
            tableColumnMap === TableColumnMap.TaskFields ||
            tableColumnMap === TableColumnMap.BuyerListingOSLFields
          ) {
            return {
              ...col,
              renderCell: (params: any) => (
                <div
                  style={{
                    cursor: "pointer",
                  }}
                >
                  {params.value}
                </div>
              ),
            };
          } else {
            return {
              ...col,
              renderCell: (params: any) => (
                <div style={{ color: "black" }}>{params.value}</div>
              ),
            };
          }
        }
        return col;
      });

      return hoverColorColumn;
    },
    [getSortedColumns, showMilestoneProgress]
  );

  return {
    setBuyerlistingTables,
    handleEdit,
    getSortedColumns,
    handleChange,
    handleChangeText,
    handleChangeDates: handleChangeDate,
    getHomesiteGridColumns,
    homesiteColumns,
    handleNHCDateTime,
    handleDateTimeChange,
  };
};


